import React from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { useAppDispatch } from '@/app/store';
import { initializeSidebarState, setSidebarWidth } from '../uiSlice';
import { Header } from '../Header/Header';
import { Sidebar } from '../Sidebar/Sidebar';
import { CreativeDetailsModal, AddTargetGroupModal } from '@/features/dooh';
import ImagePreviewModal from '../ImagePreviewModal';
import ConsolidatedReportModal from '../ConsolidatedReportModal';
interface AppLayoutProps {
  children: React.ReactNode;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Initialize sidebar state on mount and update when screen size changes
  React.useEffect(() => {
    dispatch(initializeSidebarState({ isMobile }));
  }, [dispatch, isMobile]);

  // Update sidebar width based on screen size
  React.useEffect(() => {
    const newWidth = isTablet ? 220 : 240;
    dispatch(setSidebarWidth(newWidth));
  }, [dispatch, isTablet]);

  // Responsive header heights for compact design
  const getHeaderHeight = () => {
    if (isMobile) return '40px'; // Extra compact on mobile (md and below)
    if (isTablet) return '44px'; // Compact on tablets (lg and below)
    return '48px'; // Compact on desktop (xl and above)
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header - Fixed at top */}
      <Header showMenuButton={true} />

      {/* Content Area - Sidebar + Main Content */}
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          height: `calc(100vh - ${getHeaderHeight()})`, // Take remaining height after header
          overflow: 'hidden', // Prevent layout issues
        }}
      >
        {/* Sidebar */}
        <Sidebar variant={isMobile ? 'temporary' : 'persistent'} />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: 0, // Important: allows flex item to shrink properly
            overflow: 'auto', // Allow scrolling within main content
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              p: { xs: 2 }, // Responsive padding
              height: '100%',
              overflow: 'auto', // Allow content to scroll if needed
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>

      {/* Global Modals */}
      <CreativeDetailsModal />
      <ImagePreviewModal />
      <AddTargetGroupModal />
      <ConsolidatedReportModal />
    </Box>
  );
};
